import {
  Control,
  Controller,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useEffect, useRef } from "react";
import { Button } from "@/components/bnp-ui";
import { Search } from "@/components/bnp-icons";
import { IconX } from "@tabler/icons-react";

interface SearchFieldValues {
  search?: string;
  location?: string;
}

interface SearchFilterProps {
  control: Control<SearchFieldValues>;
  setValue: UseFormSetValue<SearchFieldValues>;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
}

const DEBOUNCE_MS = 600;

const SearchFilter = ({ control, setValue, onSubmit }: SearchFilterProps) => {
  const router = useRouter();
  const search = useWatch({ control, name: "search" });
  const location = useWatch({ control, name: "location" });

  const activeSearch =
    typeof router.query.search === "string" ? router.query.search : "";
  const activeLocation =
    typeof router.query.location === "string" ? router.query.location : "";
  const hasActiveFilter = Boolean(activeSearch || activeLocation);

  // Skip submitting on initial mount to avoid duplicating the initial query
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      onSubmit();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, location]);

  const handleClear = () => {
    setValue("search", "");
    setValue("location", "");
    router.replace({ pathname: router.pathname });
  };

  return (
    <section className="mt-8 mb-6">
      <div className="bg-[#0c2e3a] text-white rounded-3xl p-6 md:p-8 shadow-2xl border border-[#163e4c]">
        <form
          onSubmit={onSubmit}
          className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-end"
        >
          <Controller
            name="search"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <div className="flex-1 md:w-[300px] flex flex-col gap-2">
                <label className="text-xs font-bold text-white tracking-wide">
                  Name
                </label>
                <input
                  type="text"
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value || ""}
                  placeholder="Search by name"
                  className="w-full bg-[#0a2530] text-white border border-white/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#1fa3b3] placeholder-white/40"
                />
              </div>
            )}
          />

          <Controller
            name="location"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <div className="flex-1 md:w-[300px] flex flex-col gap-2">
                <label className="text-xs font-bold text-white tracking-wide">
                  Location
                </label>
                <input
                  type="text"
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value || ""}
                  placeholder="Search by location"
                  className="w-full bg-[#0a2530] text-white border border-white/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#1fa3b3] placeholder-white/40"
                />
              </div>
            )}
          />

          <Button className="w-full md:w-auto bg-[#1fa3b3] hover:bg-[#188c9a] font-sans text-white font-bold h-[48px] rounded-xl transition-colors duration-150">
            <div className="flex justify-center items-center gap-2">
              <Search /> Search
            </div>
          </Button>
        </form>

        {hasActiveFilter && (
          <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-white/10">
            <span className="text-xs font-semibold text-white/70">
              Active filters:
            </span>
            {activeSearch && (
              <span className="inline-flex items-center gap-1.5 bg-[#0a2530] border border-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                Search: {activeSearch}
              </span>
            )}
            {activeLocation && (
              <span className="inline-flex items-center gap-1.5 bg-[#0a2530] border border-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                Location: {activeLocation}
              </span>
            )}
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1fa3b3] hover:bg-[#188c9a] text-white px-3 py-1.5 rounded-lg transition-colors duration-150"
            >
              <IconX className="w-3.5 h-3.5" />
              Clear filter
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchFilter;
